import { useEffect, useState, useRef } from "react";
import styles from "./ChatSection.module.css";
import useDashboardStore from "../../store/dashboardStore";
import chatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { FiSearch } from "react-icons/fi";
import MessageCard from "./MessageCard";
import EmojiPicker from "emoji-picker-react";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

import {
  FaLock,
  FaUnlock,
  FaVolumeUp,
  FaVolumeMute,
  FaArrowDown,
  FaSpinner,
} from "react-icons/fa";
import axios from "axios";
import { formatDateTimeParts } from "./MessageCard";

const MESSAGES_PER_PAGE = 20;

const ChatSection = () => {
  const { user, userMeta, socket } = useAuthStore();
  const {
    channels,
    setChannels,
    selectedChannel,
    setSelectedChannel,
    users,
    setUsers,
    userJoinedChannels,
    setUserJoinedChannels,
  } = useDashboardStore();
  const {
    unreadChatCounts,
    setUnreadChatCounts,
    newMessageRecieved,
    setNewMessageRecieved,
    newChatPermissionRecieved,
    setNewChatPermissionRecieved,
    notificationPermissionList,
    setNotificationPermissionList,
    newChatNotificationRecieved,
    setNewChatNotificationRecieved,
  } = chatStore();

  const [isAccessible, setIsAccessible] = useState(false);
  const [amIAssignedToThisChannel, setAmIAssignedToThisChannel] =
    useState(false);
  const [loading, setLoading] = useState(true);
  const [joinedRoom, setJoinedRoom] = useState(false);

  const [messages, setMessages] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [scrollToBottom, setScrollToBottom] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const [isSending, setIsSending] = useState(false);

  const messageListRef = useRef(null);
  const unreadRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const inputRef = useRef(null);

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  // Debounce ref to prevent rapid consecutive sends
  const lastSendTime = useRef(0);
  const SEND_DEBOUNCE_MS = 500; // Minimum time between sends

  //To show the unread-message count
  const [retainedUnreadCount, setRetainedUnreadCount] = useState(null);

  const [linkPreview, setLinkPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const [uploading, setUploading] = useState(false); // state for loader

  /// ---------------------------------------------- Search Mode ------------------------------------------------
  const [searchMode, setSearchMode] = useState(false);
  const [contextMessages, setContextMessages] = useState([]);
  const [contextTarget, setContextTarget] = useState(null); // id of the clicked search result
  const searchRef = useRef(null);

  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  // Add highlight state
  const [highlightedMessageId, setHighlightedMessageId] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const [channelDetails, setChannelDetails] = useState({});

  const handleEditMessage = async (messageId, newText) => {
    try {
      console.log("Editing message:", { messageId, newText, userId: user.id });

      const res = await api.put(ENDPOINTS.EDIT_MESSAGE(messageId), {
        newMessage: newText,
        userId: user.id,
      });

      if (res.data.success) {
        // Update local state
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  message: newText,
                  updated_at: new Date().toISOString(),
                }
              : msg
          )
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    message: newText,
                    updated_at: new Date().toISOString(),
                  }
                : msg
            )
          );
        }

        // Emit socket event
        socket.emit("message-edited", {
          channelId: selectedChannel.id,
          messageId,
          newMessage: newText,
          userId: user.id,
        });

        toast.success("Message updated successfully");
      }
    } catch (error) {
      console.error("Failed to edit message:", error);
      console.error("Error response:", error.response?.data);
      toast.error(error.response?.data?.message || "Failed to edit message");
    }
  };

  const handleDeleteMessage = (messageId) => {
    confirmAlert({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this message?",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            try {
              const res = await api.delete(
                ENDPOINTS.DELETE_MESSAGE(messageId),
                {
                  data: { userId: user.id },
                }
              );

              if (res.data.success) {
                // Update local state
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === messageId ? { ...msg, is_deleted: 1 } : msg
                  )
                );

                // Update context messages if in search mode
                if (searchMode) {
                  setContextMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === messageId ? { ...msg, is_deleted: 1 } : msg
                    )
                  );
                }

                // Emit socket event
                socket.emit("message-deleted", {
                  channelId: selectedChannel.id,
                  messageId,
                  userId: user.id,
                });

                toast.success("Message deleted successfully");
              }
            } catch (error) {
              console.error("Failed to delete message:", error);
              toast.error(
                error.response?.data?.message || "Failed to delete message"
              );
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.info("Message deletion cancelled"),
        },
      ],
    });
  };

  useEffect(() => {
    if (!socket) return;

    // Listen for message edits
    const handleMessageEdited = (data) => {
      const { messageId, newMessage, channelId } = data;

      // Only update if it's for the current channel
      if (channelId === selectedChannel.id) {
        // Update normal messages
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? {
                  ...msg,
                  message: newMessage,
                  updated_at: new Date().toISOString(),
                }
              : msg
          )
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId
                ? {
                    ...msg,
                    message: newMessage,
                    updated_at: new Date().toISOString(),
                  }
                : msg
            )
          );
        }
      }
    };

    // Listen for message deletes
    const handleMessageDeleted = (data) => {
      const { messageId, channelId } = data;

      // Only update if it's for the current channel
      if (channelId === selectedChannel.id) {
        // Update normal messages
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId ? { ...msg, is_deleted: 1 } : msg
          )
        );

        // Update context messages if in search mode
        if (searchMode) {
          setContextMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageId ? { ...msg, is_deleted: 1 } : msg
            )
          );
        }
      }
    };

    socket.on("message-edited", handleMessageEdited);
    socket.on("message-deleted", handleMessageDeleted);

    // Cleanup
    return () => {
      socket.off("message-edited", handleMessageEdited);
      socket.off("message-deleted", handleMessageDeleted);
    };
  }, [socket, selectedChannel.id, searchMode]);

  const handleMembershipClick = async () => {
    if (isPurchasing || isLoadingPlans) return;

    setIsLoadingPlans(true);

    try {
      // Get channel details to find membership plans
      const { data } = await api.get(
        ENDPOINTS.GET_CHANNEL_BY_ID(selectedChannel.id)
      );
      const channelData = data.channel || {};
      const membershipIds = channelData.membership_ids || [];

      if (membershipIds.length === 0) {
        toast.error("No membership plans configured for this channel");
        return;
      }

      if (membershipIds.length === 1) {
        // Single plan - proceed directly
        handlePurchaseMembership(membershipIds[0]);
      } else {
        // Multiple plans - fetch all memberships and filter
        const { data: membershipsData } = await api.get(
          ENDPOINTS.GET_ALL_MEMBERSHIPS
        );
        const allPlans = membershipsData.memberships || [];

        // Filter to only show plans that this channel uses
        const channelPlans = allPlans.filter((plan) =>
          membershipIds.includes(plan.id)
        );

        setAvailablePlans(channelPlans);
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error fetching channel plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  // --- helper to extract first URL ---
  const extractFirstUrl = (text) => {
    if (!text) return null;
    const urlRegex = /(https?:\/\/[^\s]+)/gi;
    const matches = text.match(urlRegex);
    return matches ? matches[0] : null;
  };

  // helper: check if user can send message
  const canUserSendMessage = () => {
    if (user?.roles[0] === "administrator") return true;
    if (user?.roles[0] === "trader" && amIAssignedToThisChannel) return true;
    return selectedChannel.allow_messages;
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;

    setIsPurchasing(true);

    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });

      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link"
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase. Please try again.");
    } finally {
      setIsPurchasing(false);
    }
  };

  //Check if Channel is Joined or Not
  const checkJoined = async () => {
    const isJoined = userJoinedChannels.filter(
      (s) => s.channel_id === selectedChannel.id
    );
    if (isJoined.length > 0) {
      setJoinedRoom(true);
    } else {
      setJoinedRoom(false);
    }
  };

  //Join a Channel and add in UserJoinedChannels list
  const joinChannel = async () => {
    try {
      const res = await api.post(
        ENDPOINTS.JOIN_CHANNEL_CHAT(selectedChannel.id),
        { userId: user.id }
      );
      if (res.data.success) {
        const { data } = await api.get(ENDPOINTS.GET_JOINED_CHANNEL(user.id));
        if (data.success) {
          const channelsJoined = data.channels || [];
          setUserJoinedChannels(channelsJoined);

          if (!socket) return;
          socket.emit("join-channels", channelsJoined, user.id);
        }
        setJoinedRoom(true);
        fetchMessages(1);
      }
    } catch (err) {
      console.error("Failed to join channel:", err);
    }
  };

  //Fetch all chats
  const fetchMessages = async (currentPage = 1) => {
    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES(
          selectedChannel.id,
          user.id,
          currentPage,
          MESSAGES_PER_PAGE
        )
      );
      const fetched = res.data.messages || [];
      if (fetched.length < MESSAGES_PER_PAGE) setHasMoreMessages(false);
      if (currentPage === 1) {
        setMessages(fetched);

        //First Time scroll to latest chat
        setTimeout(() => {
          if (unreadRef.current) {
            unreadRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          } else if (messageListRef.current != null) {
            messageListRef.current.scrollTo({
              top: messageListRef.current.scrollHeight,
              behavior: "auto",
            });
            setScrollToBottom(false);
          }
        }, 100); //to wait for DOM update
      } else {
        setMessages((prev) => [...fetched, ...prev]);
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  //Emoji Sender
  const handleEmojiClick = (emojiData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
  };

  //Send Message with protection against multiple sends
  const handleSend = async () => {
    // Prevent sending if already sending
    if (isSending) return;

    // Check for empty message
    if (!newMessage.trim()) return;

    // Debounce protection - prevent rapid consecutive sends
    const now = Date.now();
    if (now - lastSendTime.current < SEND_DEBOUNCE_MS) {
      return;
    }
    lastSendTime.current = now;

    // Set sending state
    setIsSending(true);

    try {
      const payload = {
        userId: user.id,
        message: newMessage.trim(),
        fileType: "other",
        fileUrl: null,
        senderName: user.username,
      };

      const res = await api.post(
        ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
        payload
      );

      if (res.data.success) {
        // Clear message and preview immediately after successful send
        const messageToSend = newMessage.trim();
        setNewMessage("");
        setLinkPreview(null);

        socket.emit("send-message", {
          id: res.data.data.id,
          channelId: selectedChannel.id,
          userId: user.id,
          message: payload,
          channelDetails: channelDetails,
        });

        // Focus the input field after successful send
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 100);
      } else {
        // If API returns unsuccessfully, show error but don't clear message
        toast.error("Failed to send message. Please try again.");
      }
    } catch (err) {
      console.error("Failed to send message:", err);
      toast.error("Failed to send message. Please try again.");
    } finally {
      // Always reset sending state
      setIsSending(false);
    }
  };

  // Enhanced key handler with protection
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (e.shiftKey) {
        // allow newline
        return;
      }
      e.preventDefault(); // Prevent form submission or other default behavior
      if (canUserSendMessage() && !isSending) {
        handleSend();
      }
    }
  };

  //Scroll top to fetch more data
  const handleScroll = () => {
    const container = messageListRef.current;
    if (!container || !hasMoreMessages) return;

    if (container.scrollTop === 0) {
      const prevHeight = container.scrollHeight;

      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage).then(() => {
        requestAnimationFrame(() => {
          const newHeight = container.scrollHeight;
          container.scrollTop = newHeight - prevHeight;
        });
      });
    }
  };

  const ensureUserExists = async (userId) => {
    const exists = users.some((u) => u.id === userId);
    if (exists) return true;

    try {
      const { data } = await api.post(ENDPOINTS.GET_ALL_USERS_SIMPLE_BY_IDS, {
        user_ids: [userId],
      });
      if (data.success && Array.isArray(data.users)) {
        const newUsers = data.users.map((u) => ({
          id: u.id,
          username: u.username || u.name || "",
        }));
        let updatedUsersList = [...users, ...newUsers];
        setUsers(updatedUsersList);
        return true;
      }
    } catch (err) {
      console.error("Failed to fetch user:", err);
    }
    return false;
  };

  //Media upload
  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true); // show loading

      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(ENDPOINTS.UPLOAD_CHAT_MEDIA, formData);
      if (res.data.success) {
        const messagePayload = {
          userId: user.id,
          message: newMessage.trim(),
          fileType: res.data.data.media_type || "other",
          fileUrl: res.data.data.file_url,
          senderName: user.username,
        };
        const resMessage = await api.post(
          ENDPOINTS.SEND_MESSAGE(selectedChannel.id),
          messagePayload
        );
        if (resMessage.data.success) {
          setNewMessage("");
          socket.emit("send-message", {
            id: resMessage.data.data.id,
            channelId: selectedChannel.id,
            userId: user.id,
            message: messagePayload,
            channelDetails: channelDetails,
          });

          // Focus the input field after successful media send
          setTimeout(() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }, 100);
        }
      }

      setUploading(false); // hide loading
    }
  };

  // Toggle Chat Permission
  const toggleChatPermission = async () => {
    try {
      const { data } = await api.put(
        ENDPOINTS.TOGGLE_CHAT_PERMISSION(selectedChannel.id)
      );
      if (data.success) {
        toast.success(data.message || "permission updated successfully");
        socket.emit("chat-permission-toggle", {
          channelId: selectedChannel.id,
          allow_messages: selectedChannel.allow_messages ? false : true,
        });
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Error updating chat permission");
    }
  };

  // Toggle Chat Notification Permission
  const toggleChatNotificationPermission = async () => {
    try {
      const { data } = await api.post(ENDPOINTS.TOGGLE_CHAT_NOTIFICATION, {
        channelId: selectedChannel.id,
        userId: user.id,
      });
      if (data.success) {
        toast.success(data.message || "notification updated successfully");
        socket.emit("chat-notification-toggle", {
          userId: user.id,
          channelId: selectedChannel.id,
          enable_notification: data.enable_notification,
        });
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Error updating chat notification");
    }
  };

  const fetchSearchResults = async (keyword) => {
    try {
      const res = await api.get(
        ENDPOINTS.SEARCH_MESSAGES(selectedChannel.id, keyword)
      );
      setSearchResults(res.data.messages || []);
    } catch (err) {
      console.error("Search failed:", err);
    }
  };

  const jumpToMessage = async (messageId) => {
    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES_BY_CONTEXT(
          selectedChannel.id,
          user.id,
          messageId,
          10,
          10
        )
      );
      const msgs = res.data.messages || [];
      setContextMessages(msgs);
      setSearchMode(true);
      setContextTarget(messageId);
      setHighlightedMessageId(messageId); // Set the message to highlight

      setTimeout(() => {
        setSearch("");
        const el = document.getElementById(`msg-${messageId}`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });

          // Remove highlight after 3 seconds
          setTimeout(() => {
            setHighlightedMessageId(null);
          }, 3000);
        }
      }, 100);
    } catch (err) {
      console.error("Jump failed:", err);
    }
  };

  const loadMoreContext = async (direction) => {
    if (!contextTarget) return;

    const anchorId =
      direction === "up"
        ? contextMessages[0]?.id
        : contextMessages[contextMessages.length - 1]?.id;

    try {
      const res = await api.get(
        ENDPOINTS.GET_MESSAGES_BY_CONTEXT(
          selectedChannel.id,
          user.id,
          anchorId,
          direction === "up" ? 10 : 0,
          direction === "down" ? 10 : 0
        )
      );

      const newMsgs = res.data.messages || [];
      if (newMsgs.length === 0) return;

      setContextMessages((prev) => {
        // merge old + new
        const merged =
          direction === "up" ? [...newMsgs, ...prev] : [...prev, ...newMsgs];

        // remove duplicates by id
        const unique = Array.from(
          new Map(merged.map((m) => [m.id, m])).values()
        );

        // keep chronological order (if needed)
        return unique.sort((a, b) => a.id - b.id);
      });
    } catch (err) {
      console.error("Load more context failed:", err);
    }
  };

  //Scroll top to fetch more search data
  const handleSearchScroll = () => {
    const container = messageListRef.current;
    if (!container) return;

    const top = container.scrollTop;
    const bottom = container.scrollHeight - top - container.clientHeight;

    if (top === 0) {
      const prevHeight = container.scrollHeight;

      loadMoreContext("up").then(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const newHeight = container.scrollHeight;
            container.scrollTop = newHeight - prevHeight;
          });
        });
      });
    } else if (bottom <= 10) {
      loadMoreContext("down");
    }
  };

  //Scroll to latest chat
  useEffect(() => {
    if (!messageListRef.current) return;
    if (!scrollToBottom) return;

    const timeout = setTimeout(() => {
      messageListRef.current.scrollTo({
        top: messageListRef.current.scrollHeight,
        behavior: "smooth",
      });
      setScrollToBottom(false);
    }, 50); //to wait for DOM update

    return () => clearTimeout(timeout);
  }, [scrollToBottom]);

  //Check if you have access to the channel
  useEffect(() => {
    if (selectedChannel?.id) {
      const checkAccess = async () => {
        setLoading(true);
        try {
          const { id, is_paid } = selectedChannel;
          const role = user?.roles?.[0];

          // Fetch channel data if needed
          let channelData = {};
          if (is_paid === 1 || role === "trader") {
            const { data } = await api.get(ENDPOINTS.GET_CHANNEL_BY_ID(id));
            channelData = data.channel || {};
            setChannelDetails(channelData);
          }

          let accessible = false;
          let isAssignedTrader = false;

          // Paid channel → check memberships
          if (is_paid === 1) {
            const membershipsAllowed = channelData.membership_ids || [];

            // Ensure userMembershipIds is always an array of numbers
            let userMembershipIds = [];
            if (Array.isArray(userMeta?.membership_ids)) {
              userMembershipIds = userMeta.membership_ids.map(Number);
            } else if (typeof userMeta?.membership_ids === "string") {
              try {
                userMembershipIds = JSON.parse(userMeta.membership_ids).map(
                  Number
                );
              } catch {
                userMembershipIds = [];
              }
            }
            accessible = membershipsAllowed.some((id) =>
              userMembershipIds.includes(id)
            );

            const tradersAllowed = channelData.trader_ids || [];
            isAssignedTrader = tradersAllowed.includes(user.id);
          } else {
            accessible = true;
          }

          // // Trader role check
          // if (role === "trader") {
          //   const tradersAllowed = channelData.trader_ids || [];
          //   isAssignedTrader = tradersAllowed.includes(user.id);
          // }

          // Trader role check
          // Trader role check
          if (role === "trader") {
            const tradersAllowed = channelData.trader_ids || [];
            isAssignedTrader = tradersAllowed.includes(user.id);

            if (!isAssignedTrader && selectedChannel.community) {
              try {
                const communityChannels = channels.filter(
                  (channel) =>
                    channel.community === selectedChannel.community &&
                    channel.id !== selectedChannel.id
                );

                if (communityChannels.length > 0) {
                  // Single API call for all channels
                  const channelIds = communityChannels.map((c) => c.id);
                  const { data } = await api.post(
                    ENDPOINTS.GET_CHANNELS_BY_IDS,
                    { channelIds }
                  );

                  const isAssignedToAnyCommunityChannel = data.channels.some(
                    (channel) => {
                      const traderIds = channel.trader_ids || [];
                      return traderIds.includes(user.id);
                    }
                  );

                  if (isAssignedToAnyCommunityChannel) {
                    accessible = true;
                  }
                }
              } catch (err) {
                console.error("Error checking community channels:", err);
              }
            }
          }
          // Admin or assigned trader always accessible
          if (
            role === "administrator" ||
            (role === "trader" && isAssignedTrader)
          ) {
            accessible = true;
          }

          setIsAccessible(accessible);
          setAmIAssignedToThisChannel(isAssignedTrader);
        } catch {
          setIsAccessible(false);
        } finally {
          setLoading(false);
        }
      };

      checkAccess();
      checkJoined();
      setPage(1);
      setHasMoreMessages(true);
      setMessages([]);
    }
  }, [
    selectedChannel?.id,
    userMeta?.membership_ids,
    selectedChannel.is_paid,
    user?.id,
  ]);

  //Automatically fetch messages after joining a channel
  useEffect(() => {
    if (joinedRoom) {
      fetchMessages(1);
    }
  }, [joinedRoom, selectedChannel.id]);

  // Get Live Messages
  useEffect(() => {
    if (!newMessageRecieved) return;

    const handleIncomingMessage = async () => {
      if (selectedChannel.id === newMessageRecieved.channelId) {
        await ensureUserExists(newMessageRecieved.message.userId);

        const formattedMessage = {
          id: newMessageRecieved.id,
          message: newMessageRecieved.message.message,
          file_type: newMessageRecieved.message.fileType,
          file_url: newMessageRecieved.message.fileUrl,
          created_at: newMessageRecieved.createdAt,
          sender_id: newMessageRecieved.message.userId,
          is_deleted: 0,
          reactions: [],
        };

        setMessages((prev) => [...prev, formattedMessage]);
        setScrollToBottom(true);
        setNewMessageRecieved(null);
        setRetainedUnreadCount(null);

        // Mark as read
        if (selectedChannel?.id) {
          await api.post(ENDPOINTS.MARK_CHANNEL_AS_READ, {
            userId: user.id,
            channelId: selectedChannel.id,
          });

          setUnreadChatCounts((prev) => ({
            ...prev,
            [selectedChannel.id]: 0,
          }));
        }
      }
    };

    handleIncomingMessage();
  }, [newMessageRecieved, selectedChannel.id]);

  useEffect(() => {
    const markAsRead = async () => {
      if (selectedChannel?.id) {
        await api.post(ENDPOINTS.MARK_CHANNEL_AS_READ, {
          userId: user.id,
          channelId: selectedChannel.id,
        });

        setUnreadChatCounts((prev) => ({
          ...prev,
          [selectedChannel.id]: 0,
        }));
      }
    };

    markAsRead();
  }, [selectedChannel.id, user.id, setUnreadChatCounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  //Get Live Channel Permission
  useEffect(() => {
    if (!newChatPermissionRecieved) return;
    if (selectedChannel.id === newChatPermissionRecieved.channelId) {
      setSelectedChannel({
        ...selectedChannel,
        allow_messages: newChatPermissionRecieved.allow_messages,
      });
      setNewChatPermissionRecieved(null);

      //Update the main channels list
      let newChannelsList = [...channels];
      const index = newChannelsList.findIndex(
        (c) => c.id === selectedChannel.id
      );
      if (index !== -1) {
        newChannelsList[index].allow_messages =
          newChatPermissionRecieved.allow_messages;
      }
      setChannels(newChannelsList);
    }
  }, [
    newChatPermissionRecieved,
    setNewChatPermissionRecieved,
    selectedChannel.id,
    selectedChannel,
    setSelectedChannel,
    channels,
    setChannels,
  ]);

  //Get Live Notification Permission
  useEffect(() => {
    if (!newChatNotificationRecieved) return;
    if (
      selectedChannel.id === newChatNotificationRecieved.channelId &&
      newChatNotificationRecieved.userId === user.id
    ) {
      let newNotificationsList = [...notificationPermissionList];
      const index = notificationPermissionList.findIndex(
        (c) => c.channel_id === selectedChannel.id
      );
      if (index !== -1) {
        newNotificationsList[index].enable_notification =
          newChatNotificationRecieved.enable_notification ? 1 : 0;
      } else {
        newNotificationsList = [
          ...notificationPermissionList,
          {
            channel_id: selectedChannel.id,
            enable_notification: newChatNotificationRecieved.enable_notification
              ? 1
              : 0,
          },
        ];
      }
      setNewChatNotificationRecieved(null);
      setNotificationPermissionList(newNotificationsList);
    }
  }, [
    selectedChannel.id,
    user.id,
    newChatNotificationRecieved,
    setNewChatNotificationRecieved,
    notificationPermissionList,
    setNotificationPermissionList,
  ]);

  useEffect(() => {
    if (selectedChannel?.id) {
      setRetainedUnreadCount(unreadChatCounts[selectedChannel.id] ?? 0);
    }
  }, [selectedChannel?.id]);
  // --- detect links while typing ---
  useEffect(() => {
    const url = extractFirstUrl(newMessage);
    if (!url) {
      setLinkPreview(null);
      return;
    }

    const fetchPreview = async () => {
      try {
        setIsPreviewLoading(true);
        const { data } = await api.get(ENDPOINTS.GET_LINK_PREVIEW(url));

        if (data.success) {
          setLinkPreview(data.preview);
        } else {
          setLinkPreview(null);
        }
      } catch (err) {
        console.error("Preview fetch error", err);
        setLinkPreview(null);
      } finally {
        setIsPreviewLoading(false);
      }
    };

    // Debounce the preview fetch
    const timeoutId = setTimeout(() => {
      fetchPreview();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newMessage]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (search.trim() !== "") {
        fetchSearchResults(search);
      } else {
        setSearchResults([]); // clear if search is empty
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounce);
  }, [search]);

  if (loading || !userMeta) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.restrictedContainer}>
          {loading ? "Checking access..." : "Loading user info..."}
        </div>
      </div>
    );
  }

  let isMuted =
    notificationPermissionList.find((e) => e.channel_id === selectedChannel.id)
      ?.enable_notification === 0;

  return (
    <div className={styles.chatContainer}>
      {!isAccessible ? (
        <div className={styles.restrictedContainer}>
          <h3>Channel Restricted</h3>
          <p>
            This channel requires a membership to access. Please purchase one
            below.
          </p>

          <button
            onClick={handleMembershipClick}
            className={styles.membershipLink}
            disabled={isPurchasing || isLoadingPlans}
          >
            {isLoadingPlans ? (
              <>
                <FaSpinner className={styles.spinner} />
                Loading Plans...
              </>
            ) : isPurchasing ? (
              <>
                <FaSpinner className={styles.spinner} />
                Processing...
              </>
            ) : (
              "Buy Membership"
            )}
          </button>
          {/* Plan Selection Modal */}
          {showPlanSelection && (
            <div className={styles.modalOverlay}>
              <div className={styles.modalContent}>
                <h3>Choose a Membership Plan</h3>
                <p>This channel offers multiple membership options:</p>

                <div className={styles.plansList}>
                  {availablePlans.map((plan) => (
                    <div key={plan.id} className={styles.planCard}>
                      <div className={styles.planInfo}>
                        <h4>{plan.name || plan.label || `Plan ${plan.id}`}</h4>
                        <div className={styles.planPrice}>
                          {plan.payment_type === "free"
                            ? "Free"
                            : `$${plan.price}`}
                        </div>
                        {plan.short_description && (
                          <p className={styles.planDescription}>
                            {plan.short_description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          handlePurchaseMembership(plan.id);
                          setShowPlanSelection(false);
                        }}
                        className={styles.planSelectButton}
                        disabled={isPurchasing}
                      >
                        {isPurchasing ? (
                          <>
                            <FaSpinner className={styles.spinner} />
                            Processing...
                          </>
                        ) : (
                          "Select This Plan"
                        )}
                      </button>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => setShowPlanSelection(false)}
                  className={styles.cancelButton}
                  disabled={isPurchasing}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : !joinedRoom ? (
        <div className={styles.restrictedContainer}>
          <h3>You're not in this channel</h3>
          <p>To join and start chatting, click below:</p>
          <button className={styles.sendButton} onClick={joinChannel}>
            Join Channel
          </button>
        </div>
      ) : (
        <>
          {/* App Bar */}
          <div className={styles.appBar}>
            <h2 className={styles.title}>{selectedChannel.name}</h2>

            {/* Group both toggle buttons together */}
            <div className={styles.buttonGroup}>
              {/* Chat Permission Button */}
              {user?.roles[0] === "administrator" && (
                // ||
                //   (user?.roles[0] === "trader" && amIAssignedToThisChannel)
                <div className={styles.toggleWrapper}>
                  <button
                    className={`${styles.toggleOption} ${
                      selectedChannel.allow_messages ? styles.activeToggle : ""
                    }`}
                    onClick={toggleChatPermission}
                  >
                    {selectedChannel.allow_messages ? <FaUnlock /> : <FaLock />}
                  </button>
                </div>
              )}

              {/* Notification Button */}
              <div className={styles.toggleWrapper}>
                <button
                  className={`${styles.toggleOption} ${
                    !isMuted ? styles.activeToggle : ""
                  }`}
                  onClick={toggleChatNotificationPermission}
                >
                  {!isMuted ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className={styles.searchContainer} ref={searchRef}>
              <input
                type="text"
                placeholder="Type in to search .."
                className={styles.searchInput}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <FiSearch className={styles.searchIcon} />

              {/* Search Popup */}
              {search && searchResults.length > 0 && (
                <div className={styles.searchPopup}>
                  {searchResults.map((msg) => (
                    <div
                      key={msg.id}
                      className={styles.searchResultItem}
                      onClick={() => jumpToMessage(msg.id)}
                    >
                      <div className={styles.resultHeader}>
                        <span className={styles.resultUser}>
                          {users.find((e) => e.id === msg.sender_id)
                            ?.username || "..."}
                        </span>
                        <span className={styles.resultTime}>
                          {formatDateTimeParts(msg.created_at).date}{" "}
                          {formatDateTimeParts(msg.created_at).time}
                        </span>
                      </div>
                      <div className={styles.resultSnippet}>{msg.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* SEARCH MESSAGE LIST */}
          {searchMode && (
            <div
              className={styles.messageList}
              onScroll={handleSearchScroll}
              ref={messageListRef}
            >
              {contextMessages.map((msg, i) => (
                <div
                  key={i}
                  id={`msg-${msg.id}`}
                  className={`${styles.messageItem} ${
                    highlightedMessageId === msg.id
                      ? styles.highlightedMessage
                      : ""
                  }`}
                >
                  <MessageCard
                    sender={
                      users.find((e) => e.id === msg.sender_id)?.username ||
                      "..."
                    }
                    messageData={msg}
                    channel={selectedChannel}
                    currentUserId={user.id}
                    currentUserRole={user?.roles[0]}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                  />
                </div>
              ))}
            </div>
          )}

          {/* NORMAL MESSAGE LIST */}
          {!searchMode && (
            <div
              className={styles.messageList}
              onScroll={handleScroll}
              ref={messageListRef}
            >
              {messages.map((msg, i) => (
                <div key={i} className={styles.messageItem}>
                  {search === "" &&
                    retainedUnreadCount > 0 &&
                    messages.length - retainedUnreadCount === i && (
                      <div ref={unreadRef} className={styles.unreadCount}>
                        {retainedUnreadCount} unread message
                      </div>
                    )}
                  <MessageCard
                    sender={
                      users.find((e) => e.id === msg.sender_id)?.username ||
                      "..."
                    }
                    messageData={msg}
                    channel={selectedChannel}
                    currentUserId={user.id}
                    currentUserRole={user?.roles[0]}
                    onEdit={handleEditMessage}
                    onDelete={handleDeleteMessage}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Footer Buttons */}
          <div className={styles.footerContainer}>
            {/* Reset Search Mode */}
            {searchMode && (
              <button
                className={styles.scrollToBottomBtn}
                onClick={() => {
                  setSearch("");
                  setSearchMode(false);
                  setContextMessages([]);
                  setContextTarget(null);
                  setHighlightedMessageId(null); // Clear highlight when exiting search mode
                  setTimeout(() => {
                    setScrollToBottom(true);
                  }, 100);
                }}
              >
                <FaArrowDown />
              </button>
            )}

            {/* Message Preview before send */}
            {isPreviewLoading && (
              <div className={styles.previewBox}>
                <p>Loading preview...</p>
              </div>
            )}
            {linkPreview && !isPreviewLoading && (
              <div className={styles.previewBox}>
                {linkPreview.image && (
                  <img
                    src={linkPreview.image}
                    alt="preview"
                    className={styles.previewImage}
                  />
                )}
                <div className={styles.previewContent}>
                  <div className={styles.previewTitle}>{linkPreview.title}</div>
                  <div className={styles.previewDesc}>
                    {linkPreview.description}
                  </div>
                  <a
                    href={linkPreview.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.previewUrl}
                  >
                    {linkPreview.url}
                  </a>
                </div>
              </div>
            )}

            {/* Send Message Box */}
            <div className={styles.inputContainer}>
              {/* Message Box */}
              <textarea
                ref={inputRef}
                placeholder="Type your message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className={styles.inputBox}
                onKeyDown={handleKeyDown}
                disabled={!canUserSendMessage() || isSending}
                rows={1}
              />
              {/* Media Upload */}
              <input
                type="file"
                id="mediaUpload"
                accept="
                  image/*,
                  video/*,
                  application/pdf,
                  application/msword,
                  application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                  application/vnd.ms-excel,
                  application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
                "
                style={{ display: "none" }}
                onChange={handleMediaUpload}
                disabled={!canUserSendMessage() || uploading || isSending}
              />
              <label
                htmlFor="mediaUpload"
                className={`${styles.sendButton} ${
                  uploading || isSending ? styles.disabled : ""
                }`}
              >
                {uploading ? <FaSpinner className={styles.spinner} /> : "+"}
              </label>

              {/* Emoji Picker */}
              <div style={{ position: "relative" }}>
                <button
                  className={`${styles.sendButton} ${
                    isSending ? styles.disabled : ""
                  }`}
                  onClick={() => setShowEmojiPicker((prev) => !prev)}
                  disabled={!canUserSendMessage() || isSending}
                >
                  😀
                </button>
                {showEmojiPicker && (
                  <div
                    ref={emojiPickerRef}
                    className={styles.emojiPickerWrapper}
                  >
                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                  </div>
                )}
              </div>

              {/* Send Button */}
              <button
                className={`${styles.sendButton} ${
                  isSending ? styles.sending : ""
                }`}
                onClick={handleSend}
                disabled={!canUserSendMessage() || isSending}
              >
                {isSending ? <FaSpinner className={styles.spinner} /> : "Send"}
              </button>
            </div>

            {/* Overlay To Disable Chat*/}
            {!canUserSendMessage() && <div className={styles.overlay}></div>}
          </div>
        </>
      )}
    </div>
  );
};

export default ChatSection;
